import { useEffect, useRef } from "react";

import Valkyrie, { viMagnifyingGlass } from "@sippy-platform/valkyrie";

import InputAdornment from "@mui/material/InputAdornment";
import InputBase from "@mui/material/InputBase";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useHotkey } from "@tanstack/react-hotkeys";
import PropTypes from "prop-types";

function Search({ onKeyPress, blur, placeholder, children, onChange, accessKey = "s", id = "search-bar", ...props }) {
  const searchRef = useRef(null);
  const isMouse = useMediaQuery("@media (hover: hover)");

  useEffect(() => {
    if (isMouse) searchRef.current.focus();
  }, [isMouse]);

  useHotkey(`Alt+${accessKey}`, () => searchRef.current.focus());

  return (
    <InputBase
      {...props}
      id={id}
      type="search"
      inputRef={searchRef}
      placeholder={placeholder ?? "Search…"}
      onChange={(e) => onChange(e.target.value)}
      sx={(theme) => ({
        width: "100%",
        height: 40,
        backgroundColor: theme.vars.palette.action.hover,
        color: "#000",
        borderRadius: 1,
        ...theme.applyStyles("dark", {
          color: "#fff",
        }),
        ".MuiInputAdornment-root": {
          pl: 2,
        },
        ".MuiInputBase-input": {
          py: 0,
          pl: 0.5,
          pr: 1.5,
          border: "none",
        },
        "&:focus-within": {
          outlineWidth: 2,
          outlineColor: theme.vars.palette.primary.main,
          outlineStyle: "solid",
          outlineOffset: -1,
        },
        ...(blur && {
          backgroundColor: theme.vars.palette.action.hover,
          backdropFilter: "blur(15px) saturate(125%)",
        }),
      })}
      inputProps={{ "aria-label": "search", onKeyPress }}
      startAdornment={
        <InputAdornment position="start">
          <Valkyrie icon={viMagnifyingGlass} />
        </InputAdornment>
      }
      endAdornment={children}
    />
  );
}

Search.propTypes = {
  onKeyPress: PropTypes.func,
  id: PropTypes.string,
  accessKey: PropTypes.string,
  placeholder: PropTypes.string,
  blur: PropTypes.bool,
  children: PropTypes.node,
  onChange: PropTypes.func,
};

export default Search;
